import React, { useState } from 'react';
import styled from 'styled-components';
import { MapPinIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import CreatableSelect from 'react-select/creatable';
import Select from 'react-select';
import { US_CITIES, US_STATES } from '../../utils/usCitiesStates.js';

const LocationContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const LocationButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !['compact'].includes(prop),
})`
  background: none;
  border: none;
  color: ${props => props.compact ? props.theme.colors.text.secondary : props.theme.colors.text.primary};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  font-size: ${props => props.compact ? props.theme.typography.small.fontSize : props.theme.typography.caption.fontSize};
  padding: ${props => props.theme.spacing.xs};
  border-radius: ${props => props.theme.borderRadius.sm};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.colors.surface};
  }
  
  svg {
    width: ${props => props.compact ? '12px' : '16px'};
    height: ${props => props.compact ? '12px' : '16px'};
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  box-shadow: ${props => props.theme.shadows.lg};
  z-index: ${props => props.theme.zIndex.dropdown};
  margin-top: ${props => props.theme.spacing.xs};
  width: 250px;
  max-width: 90vw;
  
  /* Responsive positioning to stay within viewport */
  @media (max-width: 768px) {
    left: 50%;
    transform: translateX(-50%);
    width: 220px;
  }
  
  /* Prevent dropdown from going off right edge */
  &.right-aligned {
    left: auto;
    right: 0;
  }
`;

const DropdownHeader = styled.div`
  padding: ${props => props.theme.spacing.md};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  font-weight: 500;
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.typography.caption.fontSize};
`;

const LocationList = styled.div`
  max-height: 300px;
  overflow-y: auto;
`;

const LocationItem = styled.button`
  width: 100%;
  background: none;
  border: none;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  text-align: left;
  color: ${props => props.theme.colors.text.primary};
  cursor: pointer;
  font-size: ${props => props.theme.typography.caption.fontSize};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.colors.surfaceVariant};
  }
  
  &.active {
    background: ${props => props.theme.colors.accent};
    color: white;
  }
`;

const CustomLocationForm = styled.div`
  padding: ${props => props.theme.spacing.md};
  border-top: 1px solid ${props => props.theme.colors.border};
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const InputContainer = styled.div`
  position: relative;
  flex: 1;
`;

const Input = styled.input`
  width: 100%;
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.sm};
  padding: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.typography.caption.fontSize};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.textMuted};
  }
`;

const SuggestionDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.sm};
  box-shadow: ${props => props.theme.shadows.md};
  z-index: ${props => props.theme.zIndex.dropdown + 1};
  max-height: 150px;
  overflow-y: auto;
`;

const SuggestionItem = styled.button`
  width: 100%;
  background: none;
  border: none;
  padding: ${props => props.theme.spacing.sm};
  text-align: left;
  color: ${props => props.theme.colors.text.primary};
  cursor: pointer;
  font-size: ${props => props.theme.typography.caption.fontSize};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.colors.surfaceVariant};
  }
  
  &:first-child {
    border-radius: ${props => props.theme.borderRadius.sm} ${props => props.theme.borderRadius.sm} 0 0;
  }
  
  &:last-child {
    border-radius: 0 0 ${props => props.theme.borderRadius.sm} ${props => props.theme.borderRadius.sm};
  }
`;

const SubmitButton = styled.button`
  background: ${props => props.theme.colors.accent};
  border: none;
  color: white;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.sm};
  cursor: pointer;
  font-size: ${props => props.theme.typography.caption.fontSize};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.colors.accentHover};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Popular cities for quick selection
const POPULAR_LOCATIONS = [
  { city: 'Gainesville', state: 'FL' },
  { city: 'Jacksonville', state: 'FL' },
  { city: 'Tampa', state: 'FL' },
  { city: 'Orlando', state: 'FL' },
  { city: 'Miami', state: 'FL' },
  { city: 'Atlanta', state: 'GA' },
  { city: 'Nashville', state: 'TN' },
  { city: 'Owensboro', state: 'KY' },
  { city: 'Austin', state: 'TX' },
  { city: 'New York', state: 'NY' },
  { city: 'Los Angeles', state: 'CA' },
  { city: 'Chicago', state: 'IL' },
  { city: 'Seattle', state: 'WA' },
];

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: '#fff',
    color: '#222',
    borderColor: state.isFocused ? '#8B5CF6' : '#ccc',
    boxShadow: state.isFocused ? '0 0 0 2px #8B5CF633' : provided.boxShadow,
    minHeight: 40,
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: '#fff',
    color: '#222',
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? '#8B5CF622' : '#fff',
    color: '#222',
    cursor: 'pointer',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#222',
  }),
  input: (provided) => ({
    ...provided,
    color: '#222',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#888',
  }),
};

const LocationPicker = ({ currentLocation, onLocationChange, compact = false }) => {
  // Default location if none provided
  const defaultLocation = { city: 'Gainesville', state: 'FL' };
  const location = currentLocation && currentLocation.city && currentLocation.state
    ? currentLocation
    : defaultLocation;
  
  const [isOpen, setIsOpen] = useState(false);
  const [customCity, setCustomCity] = useState(null);
  const [customState, setCustomState] = useState(null);
  const [dropdownAlignment, setDropdownAlignment] = useState('');
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [stateSuggestions, setStateSuggestions] = useState([]);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [showStateSuggestions, setShowStateSuggestions] = useState(false);

  const handleLocationSelect = (city, state) => {
    onLocationChange(city, state);
    // Save to localStorage for Add Show prepopulation
    localStorage.setItem('lastLocation', JSON.stringify({ city, state }));
    setIsOpen(false);
  };

  const handleToggleDropdown = (e) => {
    if (!isOpen) {
      // Check if dropdown would go off right edge of screen
      const buttonRect = e.currentTarget.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const dropdownWidth = 250;
      
      if (buttonRect.left + dropdownWidth > viewportWidth - 20) {
        setDropdownAlignment('right-aligned');
      } else {
        setDropdownAlignment('');
      }
    }
    setIsOpen(!isOpen);
  };

  const handleCityChange = (value) => {
    setCustomCity(value);
    if (value.length > 0) {
      const filtered = US_CITIES
        .filter(city => city.toLowerCase().includes(value.toLowerCase()))
        .slice(0, 5);
      setCitySuggestions(filtered);
      setShowCitySuggestions(true);
    } else {
      setShowCitySuggestions(false);
    }
  };

  const handleStateChange = (value) => {
    setCustomState(value.toUpperCase());
    if (value.length > 0) {
      const filtered = US_STATES
        .filter(state => state.toLowerCase().includes(value.toLowerCase()))
        .slice(0, 5);
      setStateSuggestions(filtered);
      setShowStateSuggestions(true);
    } else {
      setShowStateSuggestions(false);
    }
  };

  const handleCitySuggestionSelect = (city) => {
    setCustomCity(city);
    setShowCitySuggestions(false);
  };

  const handleStateSuggestionSelect = (state) => {
    setCustomState(state);
    setShowStateSuggestions(false);
  };

  const handleCustomLocationSubmit = (e) => {
    e.preventDefault();
    if (customCity && customState) {
      onLocationChange(customCity.value, customState.value);
      // Save to localStorage for Add Show prepopulation
      localStorage.setItem('lastLocation', JSON.stringify({ city: customCity.value, state: customState.value }));
      setCustomCity(null);
      setCustomState(null);
      setIsOpen(false);
    }
  };

  const isCurrentLocation = (city, state) => {
    return location.city === city && location.state === state;
  };

  return (
    <LocationContainer>
      <LocationButton
        compact={compact}
        onClick={handleToggleDropdown}
      >
        <MapPinIcon />
        <span>{location.city}, {location.state}</span>
        <ChevronDownIcon style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
      </LocationButton>

      {isOpen && (
        <Dropdown className={dropdownAlignment}>
          <DropdownHeader>
            Select Location
          </DropdownHeader>
          
          <LocationList>
            {POPULAR_LOCATIONS.map((location) => (
              <LocationItem
                key={`${location.city}-${location.state}`}
                className={isCurrentLocation(location.city, location.state) ? 'active' : ''}
                onClick={() => handleLocationSelect(location.city, location.state)}
              >
                {location.city}, {location.state}
              </LocationItem>
            ))}
          </LocationList>

          <CustomLocationForm>
            <form onSubmit={handleCustomLocationSubmit}>
              <FormRow>
                <InputContainer>
                  <CreatableSelect
                    options={US_CITIES}
                    value={customCity}
                    onChange={option => setCustomCity(option)}
                    placeholder="Select or type your city..."
                    isClearable
                    styles={customSelectStyles}
                  />
                </InputContainer>
                <InputContainer>
                  <Select
                    options={US_STATES}
                    value={customState}
                    onChange={option => setCustomState(option)}
                    placeholder="Select your state..."
                    isClearable
                    styles={customSelectStyles}
                  />
                </InputContainer>
              </FormRow>
              <SubmitButton
                type="submit"
                disabled={!customCity || !customState}
              >
                Set Location
              </SubmitButton>
            </form>
          </CustomLocationForm>
        </Dropdown>
      )}

      {isOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999
          }}
          onClick={() => setIsOpen(false)}
        />
      )}
    </LocationContainer>
  );
};

export default LocationPicker; 